import {
    ExclamationTriangleIcon as AlertTriangleIcon,
    CheckCircleIcon as CircleCheckIcon,
    CircleDashedIcon,
    Clock3hIcon,
} from "@wags/ui/product-icons.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@wags/ui/tooltip.tsx";
import { useState } from "react";

import type { CareChip, CareStatus, ListingHealth } from "./listing-health";

const CARE_STATUS_DETAILS: Record<CareStatus, string> = {
    recorded: "Reported as completed in the listing.",
    promised: "Listed as planned before going home.",
    not_listed: "Status unknown; ask the provider.",
    special: "Medical note listed; ask the provider for details.",
};

function CareGlyph({ status }: { status: CareStatus }) {
    if (status === "recorded") {
        return (
            <CircleCheckIcon className="size-5 shrink-0" aria-hidden="true" />
        );
    }
    if (status === "promised") {
        return <Clock3hIcon className="size-5 shrink-0" aria-hidden="true" />;
    }
    if (status === "special") {
        return (
            <AlertTriangleIcon className="size-5 shrink-0" aria-hidden="true" />
        );
    }
    return <CircleDashedIcon className="size-5 shrink-0" aria-hidden="true" />;
}

function CareChipView({ chip }: { chip: CareChip }) {
    const [open, setOpen] = useState(false);
    const statusClassName =
        chip.status === "recorded"
            ? "bg-status-success-surface text-status-success-foreground"
            : chip.status === "promised"
              ? "bg-status-promise-surface text-status-promise-foreground"
              : chip.status === "not_listed"
                ? "bg-greige text-[color-mix(in_srgb,var(--doberman-black)_65%,var(--greige))]"
                : "bg-irish-setter/12 text-chocolate-lab";

    return (
        <li className="max-w-full">
            <Tooltip open={open} onOpenChange={setOpen} delayDuration={300}>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onPointerDown={(event) => {
                            if (event.pointerType === "touch") {
                                event.preventDefault();
                            }
                        }}
                        onPointerLeave={(event) => {
                            if (event.pointerType === "touch") {
                                event.preventDefault();
                            }
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            setOpen(!open);
                        }}
                        className={`inline-flex min-h-11 max-w-full cursor-help items-center gap-1.5 rounded-full py-2 pl-3 pr-4 text-left font-family-body text-sm font-medium leading-5 tracking-[-0.32px] outline-offset-2 transition-shadow hover:ring-1 hover:ring-current focus-visible:outline-2 focus-visible:outline-current data-[state=instant-open]:ring-1 data-[state=instant-open]:ring-current data-[state=delayed-open]:ring-1 data-[state=delayed-open]:ring-current motion-reduce:transition-none ${statusClassName}`}
                    >
                        <CareGlyph status={chip.status} />
                        {chip.label}
                    </button>
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    sideOffset={8}
                    collisionPadding={16}
                    className="max-w-[min(17rem,calc(100vw-2rem))] px-3 py-2 text-left text-sm leading-5 text-pretty data-[state=instant-open]:animate-none motion-reduce:animate-none"
                >
                    {CARE_STATUS_DETAILS[chip.status]}
                </TooltipContent>
            </Tooltip>
        </li>
    );
}

export function ListingHealthSection({ health }: { health: ListingHealth }) {
    const caption =
        health.tone === "promised"
            ? "Promised before pickup"
            : "From the listing";

    return (
        <section className="mt-10 flex flex-col gap-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                <h2 className="text-section-title text-doberman-black">
                    Health & wellness
                </h2>
                {health.care.length > 0 && (
                    <span className="shrink-0 text-body-small text-doberman-black/60">
                        {caption}
                    </span>
                )}
            </div>
            {health.care.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                    {health.care.map((chip) => (
                        <CareChipView
                            key={`${chip.key}-${chip.status}-${chip.label}`}
                            chip={chip}
                        />
                    ))}
                </ul>
            )}
            {health.evaluations.length > 0 && (
                <div className="flex flex-col gap-2">
                    <p className="text-body-small text-doberman-black/60">
                        {health.evaluations.length === 1
                            ? "1 parent evaluation on file"
                            : `${health.evaluations.length} parent evaluations on file`}
                    </p>
                    <ul className="flex flex-wrap gap-2">
                        {health.evaluations.map((evaluation) => (
                            <li
                                key={evaluation}
                                className="inline-flex h-10 items-center whitespace-nowrap rounded-label bg-havanese px-4 font-family-body text-sm font-medium leading-4 tracking-[-0.32px] text-doberman-black"
                            >
                                {evaluation}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {health.note && (
                <p className="max-w-[65ch] text-pretty text-body text-doberman-black/70">
                    {health.note}
                </p>
            )}
        </section>
    );
}
